/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import type { TableOfContentsEntry } from '@lexical/react/LexicalTableOfContentsPlugin';
// import type { HeadingTagType } from "@lexical/rich-text";
import type { NodeKey } from 'lexical';
import CollapseIcon from '../../../../assets/collapse-icon.svg';
import ArrowUpIcon from '../../../../assets/arrow-up-icon.svg';
import CloseIcon from '../../../../assets/close-green-icon.svg';

import './index.css';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { TableOfContentsPlugin as LexicalTableOfContentsPlugin } from '@lexical/react/LexicalTableOfContentsPlugin';
import { useRef, useState } from 'react';
import * as React from 'react';
import { useAtom } from 'jotai';
import { collapseDesktopAtom, collapseMobileAtom, openAtom } from './atom';
import { useEditorState } from '../../BaseEditor/hooks/useBaseEditorFormatState';

function TableOfContentsPreviewList({
  tableOfContents
}: {
  tableOfContents: Array<TableOfContentsEntry>;
}): JSX.Element {
  const [selectedKey, setSelectedKey] = useState('');
  const [isOpen, setIsOpen] = useAtom(openAtom);
  const selectedIndex = useRef(0);
  const [editor] = useLexicalComposerContext();
  const SECTION_HEADER = ['h1', 'h2', 'h3'];

  function scrollToNode(key: NodeKey, currIndex: number) {
    editor.update(() => {
      // Remove highlight from all headings
      document
        .querySelectorAll('.TwygsEditorTheme__h1, .TwygsEditorTheme__h2, .TwygsEditorTheme__h3')
        .forEach((el) => {
          el.classList.remove('highlighted-heading');
        });

      // Highlight the selected heading
      const domElement = editor.getElementByKey(key);
      if (domElement !== null) {
        domElement.classList.add('highlighted-heading');
        const headingOffset = 50; // Adjust this value to match the heading height
        const topPosition = domElement.getBoundingClientRect().top + window.scrollY - headingOffset;

        window.scrollTo({
          top: topPosition,
          behavior: 'smooth'
        });

        setSelectedKey(key);
        selectedIndex.current = currIndex;
      }
    });
  }

  function scrollToTop() {
    if (window) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }

  return (
    <div className="fixed z-20 top-0 left-4 md:left-[100px] !font-ui">
      {!isOpen && (
        <div className={`preview-table-of-contents ${isOpen ? 'hidden' : 'flex'} md:!flex`}>
          <div className="flex flex-col gap-4">
            <div className="w-full flex gap-[10px] pb-2 border-b border-earth-brown-50">
              <img
                src={CloseIcon}
                className="cursor-pointer"
                alt=""
                onClick={() => setIsOpen(!isOpen)}
              />
              <p className="font-[800] text-01-jet-black text-[20px]">Contents</p>
            </div>

            <div
              className="flex items-center gap-2 font-[700] text-sm !text-growth-green-500 cursor-pointer hover:opacity-80"
              onClick={scrollToTop}
            >
              Back to top <img src={ArrowUpIcon} alt="" />
            </div>
            <ul className="preview-headings">
              {tableOfContents.map(([key, text, tag], index) => {
                if (!SECTION_HEADER.includes(tag)) {
                  return null;
                }

                return (
                  <div className={`normal-heading-wrapper`} key={key}>
                    <div onClick={() => scrollToNode(key, index)} role="button" tabIndex={0}>
                      {SECTION_HEADER.includes(tag) && (
                        <li
                          className={`normal-heading ${selectedKey === key ? 'selected-heading' : ''}`}
                        >
                          <p
                            className={`${selectedKey === key ? 'selected-heading' : ''} text-sm font-[900] line-clamp-2`}
                          >
                            {text}
                          </p>
                        </li>
                      )}
                    </div>
                  </div>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function TableOfContentsList({
  tableOfContents
}: {
  tableOfContents: Array<TableOfContentsEntry>;
}): JSX.Element {
  const [selectedKey, setSelectedKey] = useState('');
  const [collapseMobile, setCollapseMobile] = useAtom(collapseMobileAtom);
  const [collapseDesktop, setCollapseDesktop] = useAtom(collapseDesktopAtom);
  const selectedIndex = useRef(0);
  const [editor] = useLexicalComposerContext();
  const SECTION_HEADER = ['h1', 'h2', 'h3'];

  function scrollToNode(key: NodeKey, currIndex: number) {
    editor.update(() => {
      // Remove highlight from all headings
      document
        .querySelectorAll('.TwygsEditorTheme__h1, .TwygsEditorTheme__h2, .TwygsEditorTheme__h3')
        .forEach((el) => {
          el.classList.remove('highlighted-heading');
        });

      // Highlight the selected heading
      const domElement = editor.getElementByKey(key);
      if (domElement !== null) {
        domElement.classList.add('highlighted-heading');
        const headingOffset = 50; // Adjust this value to match the heading height
        const topPosition = domElement.getBoundingClientRect().top + window.scrollY - headingOffset;

        window.scrollTo({
          top: topPosition,
          behavior: 'smooth'
        });

        setSelectedKey(key);
        selectedIndex.current = currIndex;
      }
    });
  }

  function scrollToTop() {
    if (window) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }

  return (
    <div
      className={`table-of-contents w-[256px] mt-[56px] ${collapseDesktop ? 'md:w-[80px]' : 'md:w-[256px]'} ${collapseMobile ? 'hidden' : 'flex'} md:!flex !font-ui`}
    >
      <div
        className="p-[10px] block md:hidden hover:opacity-80 cursor-pointer"
        onClick={() => {
          setCollapseMobile(!collapseMobile);
        }}
      >
        <img src={CollapseIcon} className="rotate-180" alt="collapse" />
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <p
            className={`font-[800] text-sm text-earth-brown-900 ${collapseDesktop ? 'md:hidden' : ''}`}
          >
            Table of Contents
          </p>
          <div
            className="p-[10px] hidden md:!block hover:opacity-80 cursor-pointer"
            onClick={() => {
              setCollapseDesktop(!collapseDesktop);
            }}
          >
            <img
              src={CollapseIcon}
              className={`${collapseDesktop ? '' : 'rotate-180'} w-5 h-5`}
              alt="collapse"
            />
          </div>
        </div>
        <div
          className={`flex items-center gap-2 font-[700] text-sm !text-growth-green-500 cursor-pointer hover:opacity-80 ${collapseDesktop ? 'md:hidden' : ''}`}
          onClick={scrollToTop}
        >
          Back to top <img src={ArrowUpIcon} alt="" />
        </div>
        <ul className="headings">
          {tableOfContents.map(([key, text, tag], index) => {
            if (!SECTION_HEADER.includes(tag)) {
              return null;
            }

            return (
              <div
                className={`normal-heading-wrapper ${collapseDesktop ? 'md:hidden' : ''}`}
                key={key}
              >
                <div onClick={() => scrollToNode(key, index)} role="button" tabIndex={0}>
                  {SECTION_HEADER.includes(tag) && (
                    <li
                      className={`normal-heading ${selectedKey === key ? 'selected-heading' : ''}`}
                    >
                      <p
                        className={`${selectedKey === key ? 'selected-heading' : ''} text-sm font-[900] line-clamp-2`}
                      >
                        {text}
                      </p>
                    </li>
                  )}
                </div>
              </div>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export default function TableOfContentsPlugin() {
  const [editor] = useLexicalComposerContext();

  const { isEditable } = useEditorState(editor as any);

  return (
    <LexicalTableOfContentsPlugin>
      {(tableOfContents) => {
        return isEditable ? (
          <TableOfContentsList tableOfContents={tableOfContents} />
        ) : (
          <TableOfContentsPreviewList tableOfContents={tableOfContents} />
        );
      }}
    </LexicalTableOfContentsPlugin>
  );
}
